import { DicomDataset, DicomElement } from './dataset';
import {
    FILE_META_INFORMATION_VERSION_TAG,
    IMPLEMENTATION_CLASS_UID_TAG,
    IMPLEMENTATION_VERSION_NAME_TAG,
    MEDIA_STORAGE_SOP_CLASS_UID_TAG,
    MEDIA_STORAGE_SOP_INSTANCE_UID_TAG,
    SOP_CLASS_UID_TAG,
    SOP_INSTANCE_UID_TAG,
    Tag,
    TRANSFER_SYNTAX_UID_TAG,
} from './tag';

/**
 * Building the PS3.10 file meta group for a dataset that has none.
 *
 * Two callers need it: a dataset assembled in memory, which never had a meta
 * group, and one read from a stored dataset that was written without one. Both
 * describe the same dataset in the same way, so the group is built in one
 * place — a second builder is a second chance for the group to disagree with
 * the object it describes.
 *
 * Every attribute is sourced from the dataset or from the syntax the dataset
 * is written under. None is invented: where the dataset states no SOP class or
 * instance, the meta attribute is written empty rather than filled with a
 * plausible value, because a meta group that names a different object than the
 * dataset it heads is worse than one that names none.
 */

/** The implementation identity a written file claims as its origin. */
export interface ImplementationIdentity {
    readonly classUid: string;
    readonly versionName: string;
}

/**
 * This device's own identity, claimed by a file whose meta group this engine
 * built from a dataset that arrived without one.
 */
export const WORKSPACE_IMPLEMENTATION: ImplementationIdentity = {
    classUid: '2.25.19722164020413952199371',
    versionName: 'MVWS',
};

/**
 * dcmjs's identity, claimed by every file the application saves.
 *
 * `datasetToDict` stamped these on its output, so they are kept for anything
 * that still goes out through the naturalized shape: swapping the writer
 * changes no byte on its own. That the application's saved files identify
 * dcmjs rather than this device is a real inconsistency, and correcting it is
 * a change to what every exported object declares — its own decision, with its
 * own traceability.
 */
export const DCMJS_IMPLEMENTATION: ImplementationIdentity = {
    classUid:
        '2.25.80302813137786398554742050926734630921603366648225212145404',
    versionName: 'dcmjs-0.0',
};

/**
 * Builds the file meta group describing `dataset`.
 *
 * The group length is not written here: the writer computes it from the bytes
 * it actually emits, so a group built now and written after an edit still
 * describes what was written.
 */
export function buildFileMeta(
    dataset: DicomDataset,
    transferSyntaxUid: string,
    implementation: ImplementationIdentity
): DicomDataset {
    const meta = new DicomDataset();

    meta.put(
        DicomElement.fromBytes(
            FILE_META_INFORMATION_VERSION_TAG,
            'OB',
            new Uint8Array([0x00, 0x01])
        )
    );
    putMetaUid(
        meta,
        MEDIA_STORAGE_SOP_CLASS_UID_TAG,
        dataset.string(SOP_CLASS_UID_TAG)
    );
    putMetaUid(
        meta,
        MEDIA_STORAGE_SOP_INSTANCE_UID_TAG,
        dataset.string(SOP_INSTANCE_UID_TAG)
    );
    putMetaUid(meta, TRANSFER_SYNTAX_UID_TAG, transferSyntaxUid);
    putMetaUid(meta, IMPLEMENTATION_CLASS_UID_TAG, implementation.classUid);
    meta.put(
        DicomElement.fromStrings(IMPLEMENTATION_VERSION_NAME_TAG, 'SH', [
            implementation.versionName,
        ])
    );
    return meta;
}

function putMetaUid(
    meta: DicomDataset,
    tag: Tag,
    value: string | undefined
): void {
    meta.put(
        DicomElement.fromStrings(tag, 'UI', value === undefined ? [] : [value])
    );
}
